import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Bot, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import PiaThread from '@/components/PiaThread';
import PiaPush from '@/components/PiaPush';
import { Sprachtest, Woerterbuch } from '@/components/PiaSprachkarten';
import PiaStimme from '@/components/PiaStimme';

/* Pia – P1 Assistenz (30.10.2026). Mitte: Chat (derselbe Thread wie das Panel auf /heute). Rechts: „Daueraufträge" (Stufe 3) und „Heute" (Termine von heute, ohne KI). Reiter „Ziele": Monatsziele (pflegt nur die Leitung). Reiter „Einstellungen": Mitteilungen auf diesem Gerät, Mail-Fallback, für den Admin die
   Empfänger des Lecks-Reports. Das CRM kann als Home-Bildschirm-App installiert werden (Manifest nur auf dieser Seite). */

const fehler = (e, std) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : std);

function Manifest() {   // nur solange die Pia-Seite offen ist: öffentliche Seiten und die übrige App bleiben unberührt
  useEffect(() => {
    const l = document.createElement('link'); l.rel = 'manifest'; l.href = '/pia-manifest.json';
    const a = document.createElement('link'); a.rel = 'apple-touch-icon'; a.href = '/pia-apple-touch-icon.png?v=2';
    document.head.append(l, a);
    return () => { l.remove(); a.remove(); };
  }, []);
  return null;
}

function Dauerauftraege() {      // Liste der Daueraufträge: pausieren, fortsetzen, löschen (Pia legt sie im Chat an, Speichern in der Karte)
  const [d, setD] = useState(undefined);
  const [weg, setWeg] = useState('');           // Löschen braucht einen zweiten Klick (kein window.confirm)
  const lade = useCallback(() => api.get('/assistenz/auftraege').then(r => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => {
    lade();
    window.addEventListener('pia-auftraege-aktualisiert', lade);
    return () => window.removeEventListener('pia-auftraege-aktualisiert', lade);
  }, [lade]);
  const tue = async (id, was) => {
    try {
      if (was === 'loeschen') await api.delete(`/assistenz/auftraege/${id}`); else await api.post(`/assistenz/auftraege/${id}/${was}`);
      setWeg(''); lade();
    } catch (e) { toast.error(fehler(e, 'Nicht möglich')); }
  };
  const liste = d?.auftraege || [];
  return (
    <Card data-testid="pia-dauerauftraege"><CardContent className="p-4 space-y-2">
      <div className="flex items-baseline justify-between"><h2 className="text-sm font-bold">Daueraufträge</h2>{d && <span className="text-xs text-muted-foreground">{d.aktiv} von {d.maximal} aktiv</span>}</div>
      {d === undefined && <Loader2 className="w-4 h-4 animate-spin" />}
      {d === null && <p className="text-sm text-muted-foreground">Daueraufträge nicht verfügbar.</p>}
      {d && liste.length === 0 && <p className="text-sm text-muted-foreground">Noch keine. Sag Pia im Chat zum Beispiel: „Schick mir ab jetzt jeden Abend um 21 Uhr, wer morgen Beratung hat.“ Sie zeigt dir die Regel, erst „Speichern“ legt sie an.</p>}
      {liste.map((a) => (
        <div key={a.id} className={`rounded-md border border-border p-2 text-sm space-y-1 ${a.aktiv ? '' : 'opacity-60'}`} data-testid="pia-auftrag">
          <div>{a.beschreibung}</div>
          <div className="text-xs text-muted-foreground">{a.aktiv ? 'läuft' : 'pausiert'}{a.erstellt_von_name ? ` · von ${a.erstellt_von_name}` : ''}{a.letzter_lauf ? ` · zuletzt ${new Date(a.letzter_lauf).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}` : ''}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => tue(a.id, a.aktiv ? 'pausieren' : 'fortsetzen')}>{a.aktiv ? 'Pausieren' : 'Fortsetzen'}</Button>
            {weg === a.id
              ? <Button size="sm" variant="destructive" onClick={() => tue(a.id, 'loeschen')}>Wirklich löschen</Button>
              : <Button size="sm" variant="ghost" onClick={() => setWeg(a.id)}>Löschen</Button>}
          </div>
        </div>
      ))}
    </CardContent></Card>
  );
}

function Heute() {      // Termine von heute aus dem Werkzeug `termine` (ohne KI), dieselben wie im Kalender
  const [d, setD] = useState(undefined);
  useEffect(() => { api.get('/assistenz/heute').then(r => setD(r.data)).catch(() => setD(null)); }, []);
  const termine = d?.termine || [];
  return (
    <Card data-testid="pia-heute"><CardContent className="p-4 space-y-2">
      <h2 className="text-sm font-bold">Heute</h2>
      {d === undefined && <Loader2 className="w-4 h-4 animate-spin" />}
      {d === null && <p className="text-sm text-muted-foreground">Heute nicht verfügbar.</p>}
      {d && termine.length === 0 && <p className="text-sm text-muted-foreground">Heute stehen keine Termine an.</p>}
      {termine.map((t, i) => (
        <p key={i} className="text-sm" data-testid="pia-heute-termin">
          <span className="font-semibold">{t.uhrzeit || '–'}</span> · {t.art_label} · {t.person?.link ? <Link to={t.person.link} className="hover:underline">{t.person.name}</Link> : (t.person?.name || 'ohne Namen')}
        </p>
      ))}
      {d?.abgeschnitten && <p className="text-xs text-muted-foreground">Weitere Termine siehe Kalender.</p>}
      <Link to="/heute" className="text-xs text-muted-foreground hover:underline">Zur Heute-Seite</Link>
    </CardContent></Card>
  );
}

const monatJetzt = () => new Date().toISOString().slice(0, 7);
const ZAHLEN = [['leads', 'Leads'], ['abschluesse', 'Abschlüsse'], ['neuumsatz', 'Neuumsatz (€)']];

function Ziele() {      // Monatsziele: alle mit Zugang lesen (Umsatz nur mit Recht), nur die Leitung pflegt
  const [monat, setMonat] = useState(monatJetzt());
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const lade = useCallback(() => api.get('/assistenz/ziele', { params: { monat } }).then(r => {
    setD(r.data); setForm(Object.fromEntries(ZAHLEN.map(([k]) => [k, r.data.ziele?.[k] ?? ''])));
  }).catch(x => toast.error(fehler(x, 'Ziele nicht geladen'))), [monat]);
  useEffect(() => { lade(); }, [lade]);
  const speichern = async () => {
    const body = { monat };
    for (const [k] of ZAHLEN) if (form[k] !== '' && form[k] != null) body[k] = Number(form[k]);
    try { await api.put('/assistenz/ziele', body); toast.success('Gespeichert'); lade(); }
    catch (x) { toast.error(fehler(x, 'Nicht gespeichert')); }
  };
  if (!d) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const felder = ZAHLEN.filter(([k]) => k !== 'neuumsatz' || d.umsatz_sichtbar);
  return (
    <Card data-testid="pia-ziele"><CardContent className="p-4 space-y-3 max-w-xl">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold">Monatsziele</h2>
        <input type="month" value={monat} onChange={(e) => e.target.value && setMonat(e.target.value)} className="border border-border rounded px-2 py-1 text-sm bg-background" aria-label="Monat" />
      </div>
      {!d.gepflegt && <p className="text-sm text-muted-foreground">{d.admin ? 'Für diesen Monat sind noch keine Ziele eingetragen.' : (d.hinweis || 'Für diesen Monat sind keine Ziele hinterlegt.')}</p>}
      <div className="space-y-2">{felder.map(([k, t]) => (
        <div key={k} className="grid grid-cols-[1fr_110px_90px] items-center gap-2 text-sm">
          <label htmlFor={`ziel-${k}`}>{t}</label>
          {d.admin
            ? <input id={`ziel-${k}`} type="number" min="0" value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="border border-border rounded px-2 py-1 bg-background" />
            : <span>{d.ziele?.[k] ?? '–'}</span>}
          <span className="text-muted-foreground text-right">Ist: {d.ist?.[k] ?? '–'}</span>
        </div>
      ))}</div>
      {d.admin && <Button size="sm" onClick={speichern} data-testid="pia-ziele-speichern">Speichern</Button>}
      {!d.admin && <p className="text-xs text-muted-foreground">Die Ziele pflegt die Leitung.</p>}
    </CardContent></Card>
  );
}

/* Pia-Finanzen (02.11.2026): Betragsgrenze für den zweiten Klick in der Freigabe-Zentrale. Die Karte gibt es nur, wenn KI_PIA_FINANZEN
   an ist und der Inhaber schaut (die Route antwortet sonst 404/403 — dann bleibt die Karte weg). */
function FinanzenGrenze() {
  const [wert, setWert] = useState(null);
  useEffect(() => { api.get('/pia-finanzen/einstellungen').then(r => setWert(String(r.data.betragsgrenze))).catch(() => setWert(null)); }, []);
  if (wert === null) return null;
  const speichern = async () => {
    try { const r = await api.put('/pia-finanzen/einstellungen', { betragsgrenze: Number(String(wert).replace(',', '.')) }); setWert(String(r.data.betragsgrenze)); toast.success('Gespeichert'); }
    catch (x) { toast.error(fehler(x, 'Nicht gespeichert')); }
  };
  return (
    <Card><CardContent className="p-4 space-y-2" data-testid="pia-finanzen-grenze">
      <h2 className="text-sm font-bold">Abrechnung: Betragsgrenze</h2>
      <p className="text-sm text-muted-foreground">Ab diesem Betrag fragt die Freigabe-Zentrale bei einer Zubuchung, Nachberechnung oder Korrektur noch einmal nach („Betrag geprüft“).</p>
      <div className="flex items-center gap-2">
        <input value={wert} onChange={(x) => setWert(x.target.value)} inputMode="decimal" className="h-8 w-28 rounded-md border border-input bg-background px-2 text-sm" />
        <span className="text-sm">€</span>
        <Button size="sm" variant="outline" onClick={speichern}>Speichern</Button>
      </div>
    </CardContent></Card>
  );
}

function Einstellungen({ status }) {
  const [e, setE] = useState(null);
  useEffect(() => { api.get('/assistenz/einstellungen').then(r => setE(r.data)).catch(x => toast.error(fehler(x, 'Nicht geladen'))); }, []);
  const speichern = async (teil) => {
    try { setE((await api.put('/assistenz/einstellungen', teil)).data); toast.success('Gespeichert'); }
    catch (x) { toast.error(fehler(x, 'Nicht gespeichert')); }
  };
  const testen = async () => {
    try { await api.post('/assistenz/push/test'); toast.success('Testnachricht ist im Chat.'); }
    catch (x) { toast.error(fehler(x, 'Nicht gesendet')); }
  };
  if (!e) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const ids = e.lecks_empfaenger || [];
  const umschalten = (uid) => speichern({ lecks_empfaenger: ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid] });
  return (
    <div className="space-y-4 max-w-xl" data-testid="pia-einstellungen">
      <Card><CardContent className="p-4 space-y-3">
        <h2 className="text-sm font-bold">Dieses Gerät</h2>
        <p className="text-sm text-muted-foreground">Mit Mitteilungen meldet sich Pia auch, wenn das CRM zu ist. Im Text stehen höchstens ein Vorname und eine Uhrzeit.</p>
        {!status.push_konfiguriert && <p className="text-xs text-amber-600">Auf dem Server sind die Push-Schlüssel noch nicht eingetragen — Mitteilungen gehen erst danach.</p>}
        <div className="flex flex-wrap items-center gap-2"><PiaPush /><Button size="sm" variant="outline" onClick={testen}>Testnachricht an mich</Button></div>
      </CardContent></Card>
      <Card><CardContent className="p-4 space-y-2">
        <h2 className="text-sm font-bold">E-Mail als Notlösung</h2>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={e.mail_fallback} onChange={(x) => speichern({ mail_fallback: x.target.checked })} data-testid="pia-mail-fallback" />
          <span>Bleibt eine Nachricht von Pia 30 Minuten ungelesen, schickt mir das CRM eine Mail mit einem Link (ohne Inhalt).</span></label>
      </CardContent></Card>
      {e.admin && (
        <Card><CardContent className="p-4 space-y-2" data-testid="pia-darf-senden">
          <h2 className="text-sm font-bold">Pia darf Nachrichten senden</h2>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={!!e.darf_senden} onChange={(x) => speichern({ darf_senden: x.target.checked })} data-testid="pia-darf-senden-schalter" />
            <span>Aus (Standard): Pia legt Nachrichten nur als Entwurf in der Freigabe-Zentrale an. An: Pia kann eine Nachricht auch senden – erst nach deinem Klick auf „Ausführen“ auf der Karte, die Empfänger, Kanal und den vollen Text zeigt. Nie per Sprache.</span></label>
        </CardContent></Card>
      )}
      {e.admin && (
        <Card><CardContent className="p-4 space-y-2" data-testid="pia-lecks-empfaenger">
          <h2 className="text-sm font-bold">Empfänger des Lecks-Reports</h2>
          <p className="text-sm text-muted-foreground">Dienstags 12:00 bekommen diese Personen den Report in ihren Pia-Thread. Sie sehen den Report in Pia (nur lesen); fragen kann Pia nur der Inhaber.</p>
          <div className="space-y-1">{(e.nutzer || []).map(u => (
            <label key={u.user_id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ids.includes(u.user_id)} onChange={() => umschalten(u.user_id)} />{u.name}</label>
          ))}</div>
        </CardContent></Card>
      )}
      {e.admin && <FinanzenGrenze />}
      <PiaStimme />
      {status.darf_fragen && <Sprachtest admin={!!e.admin} />}
      {status.darf_fragen && <Woerterbuch />}
    </div>
  );
}

export default function PiaPage() {
  const [status, setStatus] = useState(null);
  const [fehlt, setFehlt] = useState('');
  const [reiter, setReiter] = useState('chat');
  const lade = useCallback(() => api.get('/assistenz/status').then(r => setStatus(r.data)).catch(e => setFehlt(e?.response?.status === 404 ? 'Pia ist noch nicht eingeschaltet.' : 'Dafür hast du keinen Zugang.')), []);
  useEffect(() => { lade(); }, [lade]);

  if (fehlt) return <div className="p-6 text-sm text-muted-foreground" data-testid="pia-gesperrt">{fehlt}</div>;
  if (!status) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="pia-page">
      <Manifest />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Bot className="w-6 h-6 text-marke" /> Pia</h1>
          <p className="text-sm text-muted-foreground">Deine Assistenz für Leads, Termine und Zahlen. Pia schreibt nur dir, nie einem Kunden.</p>
        </div>
        <div className="flex gap-1" role="tablist">
          {[['chat', 'Chat'], ['ziele', 'Ziele'], ['einstellungen', 'Einstellungen']].map(([k, t]) => (
            <Button key={k} size="sm" variant={reiter === k ? 'default' : 'outline'} onClick={() => setReiter(k)} role="tab" aria-selected={reiter === k}>{t}</Button>
          ))}
        </div>
      </div>
      {reiter === 'einstellungen' ? <Einstellungen status={status} /> : reiter === 'ziele' ? <Ziele /> : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Card><CardContent className="p-4"><PiaThread darfFragen={status.darf_fragen} onGelesen={lade} /></CardContent></Card>
          <div className="space-y-4">
            <Dauerauftraege />
            <Heute />
            <PiaPush />
          </div>
        </div>
      )}
    </div>
  );
}
